import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '@/components/shared';
import { useBackofficePageTitle } from '@/lib/page-title';
import {
  getTopology,
  isTopologyLimitError,
  type ObservabilityRange,
} from '@/services/observability-api';
import { useObservabilityGraphStore } from '@/stores/observability-graph.store';
import {
  ArchitectureDetailsDrawer,
  ArchitectureExplorerToolbar,
  ArchitectureGraph,
  ArchitectureGraphSettingsModal,
  ArchitectureViewModeToggle,
  type MetricOverlay,
  type ObservabilityViewMode,
  type ProtocolFilter,
  type SelectedTopologyItem,
} from './components';
import { adjacentNodeIds } from './components/graph-utils';
import { useArchitectureGraphSettings } from './useArchitectureGraphSettings';
import { useDestinationIdentity } from './useDestinationIdentity';

const ranges = new Set<ObservabilityRange>(['15m', '1h', '6h', '24h']);
const protocols = new Set<ProtocolFilter>(['all', 'http', 'events', 'live']);
const overlays = new Set<MetricOverlay>(['topology', 'traffic', 'latency', 'errors']);

function readRange(value: string | null): ObservabilityRange {
  return value && ranges.has(value as ObservabilityRange) ? (value as ObservabilityRange) : '1h';
}

function readProtocol(value: string | null): ProtocolFilter {
  return value && protocols.has(value as ProtocolFilter) ? (value as ProtocolFilter) : 'all';
}

function readOverlay(value: string | null): MetricOverlay {
  return value && overlays.has(value as MetricOverlay) ? (value as MetricOverlay) : 'traffic';
}

function readNodeIds(value: string | null): string[] | null {
  if (value === null) return null;
  return [...new Set(value.split(',').filter(Boolean))];
}

function readViewMode(value: string | null): ObservabilityViewMode {
  return value === 'fullscreen' ? 'fullscreen' : 'default';
}

export function BackofficeObservabilityPage() {
  useBackofficePageTitle('Architecture observability');
  const [searchParams, setSearchParams] = useSearchParams();
  const clearPositions = useObservabilityGraphStore((state) => state.clearPositions);
  const {
    settings: graphSettings,
    loaded: graphSettingsLoaded,
    saveSettings: saveGraphSettings,
  } = useArchitectureGraphSettings();
  const [graphSettingsOpen, setGraphSettingsOpen] = useState(false);
  const [graphSettingsSaving, setGraphSettingsSaving] = useState(false);
  const [graphSettingsError, setGraphSettingsError] = useState<string | null>(null);
  const range = readRange(searchParams.get('range'));
  const protocol = readProtocol(searchParams.get('protocol'));
  const overlay = readOverlay(searchParams.get('metric'));
  const viewMode = readViewMode(searchParams.get('view'));
  const explicitNodeIds = readNodeIds(searchParams.get('nodes'));
  const adjacencySeed = searchParams.get('adjacentTo');
  const selected: SelectedTopologyItem = searchParams.get('node')
    ? { type: 'node', id: searchParams.get('node')! }
    : searchParams.get('edge')
      ? { type: 'edge', id: searchParams.get('edge')! }
      : null;
  const topologyQuery = useQuery({
    queryKey: ['observability', 'topology', range],
    queryFn: () => getTopology(range),
    staleTime: 30_000,
    refetchInterval: (query) => (isTopologyLimitError(query.state.error) ? false : 30_000),
    retry: (count, error) => !isTopologyLimitError(error) && count < 3,
    refetchOnWindowFocus: (query) => !isTopologyLimitError(query.state.error),
    refetchOnReconnect: (query) => !isTopologyLimitError(query.state.error),
    retryOnMount: false,
    placeholderData: keepPreviousData,
  });
  const topology = isTopologyLimitError(topologyQuery.error) ? undefined : topologyQuery.data;
  const identityQuery = useDestinationIdentity(topology, selected);
  const identity = {
    data: identityQuery.data,
    pending: identityQuery.isFetching,
    failed: identityQuery.isError,
    onRetry: () => {
      void identityQuery.refetch();
    },
  };
  const activeAdjacencySeed =
    adjacencySeed && topology?.nodes.some((node) => node.id === adjacencySeed)
      ? adjacencySeed
      : undefined;
  const visibleNodeIds = topology
    ? activeAdjacencySeed
      ? adjacentNodeIds(topology, protocol, activeAdjacencySeed)
      : explicitNodeIds
        ? explicitNodeIds.filter((id) => topology.nodes.some((node) => node.id === id))
        : topology.nodes.map((node) => node.id)
    : [];
  const selectedNodeIds = activeAdjacencySeed ? visibleNodeIds : (explicitNodeIds ?? []);

  const updateParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value !== null) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next, { replace: true });
  };
  const select = (item: Exclude<SelectedTopologyItem, null>) => {
    updateParams(
      item.type === 'node' ? { node: item.id, edge: null } : { edge: item.id, node: null }
    );
  };
  const setVisibleNodes = (nodeIds: string[]) => {
    updateParams({
      adjacentTo: null,
      nodes: nodeIds.length > 0 ? nodeIds.join(',') : null,
    });
  };
  const showAdjacent = (nodeId: string) => {
    if (!topology || (!activeAdjacencySeed && explicitNodeIds === null)) return;
    setVisibleNodes([
      ...new Set([...visibleNodeIds, ...adjacentNodeIds(topology, protocol, nodeId)]),
    ]);
  };
  const hideNode = (nodeId: string) => {
    const selectedEdge =
      selected?.type === 'edge'
        ? topology?.edges.find((edge) => edge.id === selected.id)
        : undefined;
    const hidesSelection =
      (selected?.type === 'node' && selected.id === nodeId) ||
      selectedEdge?.source === nodeId ||
      selectedEdge?.target === nodeId;
    updateParams({
      adjacentTo: null,
      nodes: visibleNodeIds.filter((id) => id !== nodeId).join(','),
      ...(hidesSelection ? { node: null, edge: null } : {}),
    });
  };
  const handleSaveGraphSettings = async (settings: typeof graphSettings) => {
    if (graphSettingsSaving) return;
    setGraphSettingsSaving(true);
    setGraphSettingsError(null);
    try {
      await saveGraphSettings(settings);
      setGraphSettingsOpen(false);
    } catch (error) {
      setGraphSettingsError(
        error instanceof Error ? error.message : 'Failed to save graph settings.'
      );
    } finally {
      setGraphSettingsSaving(false);
    }
  };

  const toolbar = (
    <ArchitectureExplorerToolbar
      range={range}
      protocol={protocol}
      overlay={overlay}
      nodes={topology?.nodes ?? []}
      selectedNodeIds={selectedNodeIds}
      noVisibleNodes={!activeAdjacencySeed && explicitNodeIds?.length === 0}
      adjacentTo={activeAdjacencySeed}
      onRangeChange={(value) => updateParams({ range: value })}
      onProtocolChange={(value) => updateParams({ protocol: value })}
      onOverlayChange={(value) => updateParams({ metric: value })}
      onSearchResultSelect={(id) =>
        updateParams({ node: id, edge: null, adjacentTo: null, nodes: id })
      }
      onVisibleNodesChange={setVisibleNodes}
      onResetLayout={clearPositions}
      onOpenSettings={() => {
        setGraphSettingsError(null);
        setGraphSettingsOpen(true);
      }}
    />
  );
  const graph = (className?: string) =>
    topology && topology.nodes.length > 0 ? (
      <ArchitectureGraph
        topology={topology}
        protocol={protocol}
        overlay={overlay}
        selected={selected}
        zoomOnClick={graphSettingsLoaded && graphSettings.zoomOnClick}
        onSelect={select}
        visibleNodeIds={visibleNodeIds}
        onShowAdjacent={showAdjacent}
        onHideAdjacent={(nodeId) => updateParams({ adjacentTo: null, nodes: nodeId })}
        onHideNode={hideNode}
        controlActions={viewModeToggle}
        {...(className ? { className } : {})}
      />
    ) : null;
  const viewModeToggle = (
    <ArchitectureViewModeToggle
      mode={viewMode}
      onToggle={() => updateParams({ view: viewMode === 'fullscreen' ? null : 'fullscreen' })}
    />
  );

  if (viewMode === 'default') {
    return (
      <main className="min-h-[320px] bg-white p-6 md:p-8" aria-label="Architecture observability">
        <div className="space-y-6">
          <div>
            <PageHeader showShareButton={false}>Architecture observability</PageHeader>
            <p className="mt-2 text-sm text-neutral-600">
              Live service health, request traffic, and dependencies from Prometheus metrics.
            </p>
          </div>
          {toolbar}
          {topologyQuery.isLoading && <p className="text-neutral-600">Loading topology...</p>}
          {topologyQuery.error && (
            <div role="alert" className="rounded border border-red-200 bg-red-50 p-4 text-red-800">
              {topologyQuery.error instanceof Error
                ? topologyQuery.error.message
                : 'Unable to load topology'}
              <button
                type="button"
                className="ml-3 underline"
                onClick={() => void topologyQuery.refetch()}
              >
                Retry
              </button>
            </div>
          )}
          {topology && topology.nodes.length === 0 && (
            <p className="rounded-lg border border-neutral-200 bg-white p-4 text-neutral-600">
              No service metrics were found. Confirm applications export metrics to the
              observability stack.
            </p>
          )}
          {topology && topology.nodes.length > 0 && (
            <section className="relative flex min-h-[620px] flex-col overflow-hidden rounded-lg border border-neutral-200 lg:flex-row">
              {graph()}
              <ArchitectureDetailsDrawer
                identity={identity}
                topology={topology}
                selected={selected}
                onClose={() => updateParams({ node: null, edge: null })}
                onSelectNode={(nodeId) => select({ type: 'node', id: nodeId })}
              />
            </section>
          )}
          {topology && (
            <p className="text-xs text-neutral-500">
              Updated {new Date(topology.generatedAt).toLocaleString()}
            </p>
          )}
          {graphSettingsOpen ? (
            <ArchitectureGraphSettingsModal
              initialSettings={graphSettings}
              saving={graphSettingsSaving}
              error={graphSettingsError}
              onClose={() => {
                if (!graphSettingsSaving) setGraphSettingsOpen(false);
              }}
              onSave={handleSaveGraphSettings}
            />
          ) : null}
        </div>
      </main>
    );
  }

  return (
    <main
      className="fixed inset-0 z-40 flex min-h-[640px] flex-col bg-neutral-50"
      aria-label="Architecture observability"
    >
      <header className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-neutral-200 bg-white px-4 py-3 md:px-6">
        <h1 className="text-sm font-semibold text-neutral-900">Architecture observability</h1>
        <button
          type="button"
          onClick={() => updateParams({ view: null })}
          className="rounded text-sm font-medium text-lavender-700 underline underline-offset-2 hover:text-lavender-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lavender-500"
        >
          Exit fullscreen mode
        </button>
      </header>
      <section className="relative min-h-0 flex-1 overflow-hidden">
        {graph('h-full w-full')}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-30 p-4 md:p-6">
          <div className="pointer-events-auto">{toolbar}</div>
        </div>
        {topologyQuery.isLoading && (
          <p className="absolute inset-0 grid place-items-center text-neutral-600">
            Loading topology...
          </p>
        )}
        {topologyQuery.error && (
          <div
            role="alert"
            className="absolute inset-x-4 top-28 z-20 rounded border border-red-200 bg-red-50 p-4 text-red-800"
          >
            {topologyQuery.error instanceof Error
              ? topologyQuery.error.message
              : 'Unable to load topology'}
            <button
              type="button"
              className="ml-3 underline"
              onClick={() => void topologyQuery.refetch()}
            >
              Retry
            </button>
          </div>
        )}
        {topology && topology.nodes.length === 0 && (
          <p className="absolute inset-x-4 top-28 z-20 rounded-lg border border-neutral-200 bg-white p-4 text-neutral-600">
            No service metrics were found. Confirm applications export metrics to the observability
            stack.
          </p>
        )}
        {topology && topology.nodes.length > 0 && (
          <ArchitectureDetailsDrawer
            identity={identity}
            topology={topology}
            selected={selected}
            onClose={() => updateParams({ node: null, edge: null })}
            onSelectNode={(nodeId) => select({ type: 'node', id: nodeId })}
            floating
          />
        )}
        {graphSettingsOpen ? (
          <ArchitectureGraphSettingsModal
            initialSettings={graphSettings}
            saving={graphSettingsSaving}
            error={graphSettingsError}
            onClose={() => {
              if (!graphSettingsSaving) setGraphSettingsOpen(false);
            }}
            onSave={handleSaveGraphSettings}
          />
        ) : null}
      </section>
    </main>
  );
}
